import type { Session } from "@supabase/supabase-js";
import { useEffect, useState } from "preact/hooks";
import { api, emailTestMode, must, sb } from "./lib/supabase";
import { Loading, Toasts, go, useRoute } from "./lib/ui";
import type { Membership, Role } from "./lib/types";
import { Dashboard } from "./pages/dashboard";
import { ProvidersPage, ProviderDetail, ProviderPacket } from "./pages/providers";
import { InsightsPage } from "./pages/insights";
import { SearchBox } from "./pages/search";
import { ApplicationsPage } from "./pages/applications";
import { TasksPage } from "./pages/tasks";
import { PayersPage } from "./pages/payers";
import { AdminPage } from "./pages/admin";
import { MessagesPage } from "./pages/messages";
import { BcbaPortal } from "./pages/bcba";
import { AppearanceCard, PasswordCard } from "./pages/account";

export interface Ctx { orgId: string; orgName: string; role: Role; userId: string; email: string }

export function App() {
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  useEffect(() => {
    sb().auth.getSession().then(({ data }) => setSession(data.session));
    const { data } = sb().auth.onAuthStateChange((_e, s) => setSession(s));
    return () => data.subscription.unsubscribe();
  }, []);

  if (location.pathname === "/auth/confirm") return <><ConfirmSignIn /><Toasts /></>;
  if (session === undefined) return <Loading />;
  if (!session) return <><SignIn /><Toasts /></>;
  return <><Signed session={session} /><Toasts /></>;
}

function SignIn() {
  const [email, setEmail] = useState(localStorage.getItem("lastEmail") ?? "");
  const [step, setStep] = useState<"email" | "code" | "password">("email");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const remember = () => localStorage.setItem("lastEmail", email.trim().toLowerCase());

  const sendCode = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) return setErr("Enter your email address.");
    setBusy(true); setErr(null); remember();
    await api("/api/auth/link", { email: email.trim() }).catch(() => undefined);
    setBusy(false); setStep("code");
  };

  return (
    <main class="auth">
      <h1>Credentialing Tracker</h1>
      {step === "email" && (
        <form onSubmit={(e) => { e.preventDefault(); sendCode(); }}>
          <label>Email<input type="email" required autoComplete="username" value={email} onInput={(e) => setEmail(e.currentTarget.value)} /></label>
          {err && <p class="error small">{err}</p>}
          <button disabled={busy}>Email me a sign-in code</button>
          <button type="button" class="ghost" onClick={() => {
            if (!email.trim()) return setErr("Enter your email address.");
            setErr(null); remember(); setStep("password");
          }}>Use my password</button>
        </form>
      )}
      {step === "code" && (
        <form onSubmit={async (e) => {
          e.preventDefault();
          const token = String(new FormData(e.currentTarget).get("code")).replace(/\s/g, "");
          setBusy(true); setErr(null);
          const { error } = await sb().auth.verifyOtp({ email: email.trim(), token, type: "email" });
          setBusy(false);
          if (error) setErr("That code didn't work. It may have expired or already been used — request a new one.");
        }}>
          <p>If <strong>{email}</strong> is registered, we just emailed a 6-digit code. You can also click the link in that email.</p>
          <label>Sign-in code<input name="code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9 ]{6,10}" required autoFocus /></label>
          {err && <p class="error small">{err}</p>}
          <button disabled={busy}>Sign in</button>
          <p class="small"><a href="#" onClick={(e) => { e.preventDefault(); sendCode(); }}>Send a new code</a> · <a href="#" onClick={(e) => { e.preventDefault(); setStep("email"); setErr(null); }}>Use a different email</a></p>
        </form>
      )}
      {step === "password" && (
        <form onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true); setErr(null);
          const { error } = await sb().auth.signInWithPassword({ email: email.trim(), password: String(new FormData(e.currentTarget).get("password")) });
          setBusy(false);
          if (error) setErr("Email or password is incorrect. No password yet? Sign in with a code, then set one under Account.");
        }}>
          <p class="small">Signing in as <strong>{email}</strong> · <a href="#" onClick={(e) => { e.preventDefault(); setStep("email"); setErr(null); }}>change</a></p>
          <label>Password<input name="password" type="password" required autoComplete="current-password" autoFocus /></label>
          {err && <p class="error small">{err}</p>}
          <button disabled={busy}>Sign in</button>
          <p class="small"><a href="#" onClick={(e) => { e.preventDefault(); sendCode(); }}>Forgot it? Email me a code instead</a></p>
        </form>
      )}
    </main>
  );
}

/** The emailed link lands here; the token is only used when the person clicks. */
function ConfirmSignIn() {
  const params = new URLSearchParams(location.search);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <main class="auth">
      <h1>Sign in</h1>
      {err && <p class="error">{err} <a href="/">Request a new link</a></p>}
      <button disabled={busy} onClick={async () => {
        setBusy(true);
        const { error } = await sb().auth.verifyOtp({
          token_hash: params.get("token_hash") ?? "",
          type: (params.get("type") as "magiclink") ?? "magiclink",
        });
        if (error) { setErr("This link has expired or was already used."); setBusy(false); return; }
        history.replaceState(null, "", "/");
        location.reload();
      }}>Continue to the credentialing portal</button>
    </main>
  );
}

function Signed({ session }: { session: Session }) {
  const [state, setState] = useState<{ memberships: Membership[]; isProvider: boolean } | null>(null);
  const [orgId, setOrgId] = useState<string | null>(localStorage.getItem("orgId"));
  useEffect(() => {
    (async () => {
      const memberships = must(await sb().from("org_members")
        .select("org_id, role, organizations(name)").eq("user_id", session.user.id).eq("active", true)) as unknown as Membership[];
      const prov = must(await sb().from("providers").select("id").eq("user_id", session.user.id).maybeSingle());
      setState({ memberships, isProvider: !!prov });
    })().catch(() => setState({ memberships: [], isProvider: false }));
  }, [session.user.id]);

  if (!state) return <Loading />;
  const signOut = () => sb().auth.signOut().then(() => location.assign("/"));

  if (state.memberships.length === 0) {
    if (state.isProvider) return <BcbaPortal userId={session.user.id} onSignOut={signOut} />;
    return <main class="auth"><p>Your account isn't linked to an organization yet. Ask an administrator to invite you.</p><button onClick={signOut}>Sign out</button></main>;
  }
  const m = state.memberships.find((x) => x.org_id === orgId) ?? state.memberships[0]!;
  const ctx: Ctx = { orgId: m.org_id, orgName: m.organizations.name, role: m.role, userId: session.user.id, email: session.user.email ?? "" };
  return <StaffShell ctx={ctx} memberships={state.memberships} onOrg={(id) => { localStorage.setItem("orgId", id); setOrgId(id); }} onSignOut={signOut} />;
}

const NAV: Array<[string, string, Role[]?]> = [
  ["", "Dashboard"], ["insights", "Insights"], ["providers", "BCBAs"], ["applications", "Applications"], ["tasks", "Tasks"],
  ["messages", "Messages", ["owner", "admin", "staff", "vendor"]], ["payers", "Payers"], ["admin", "Admin", ["owner", "admin"]], ["account", "Account"],
];

function StaffShell({ ctx, memberships, onOrg, onSignOut }: { ctx: Ctx; memberships: Membership[]; onOrg: (id: string) => void; onSignOut: () => void }) {
  const [page = "", id, sub] = useRoute();
  return (
    <div class="shell">
      {emailTestMode() && <div class="banner">Email test mode: every email this app sends goes to the owner's test inbox, not to the real recipient.</div>}
      <nav class="top">
        <strong class="brand">{ctx.orgName}</strong>
        {NAV.filter(([, , roles]) => !roles || roles.includes(ctx.role)).map(([path, label]) => (
          <a href={`#/${path}`} class={page === path ? "active" : ""}>{label}</a>
        ))}
        <span class="spacer" />
        <SearchBox ctx={ctx} />
        {memberships.length > 1 && (
          <select aria-label="Organization" value={ctx.orgId} onChange={(e) => onOrg(e.currentTarget.value)}>
            {memberships.map((m) => <option value={m.org_id}>{m.organizations.name}</option>)}
          </select>
        )}
        <span class="muted small">{ctx.email} · {ctx.role}</span>
        <button class="ghost" onClick={onSignOut}>Sign out</button>
      </nav>
      <main class="content">
        {page === "" && <Dashboard ctx={ctx} />}
        {page === "providers" && !id && <ProvidersPage ctx={ctx} />}
        {page === "providers" && id && sub === "packet" && <ProviderPacket ctx={ctx} id={id} />}
        {page === "providers" && id && sub !== "packet" && <ProviderDetail ctx={ctx} id={id} />}
        {page === "insights" && <InsightsPage ctx={ctx} />}
        {page === "applications" && <ApplicationsPage ctx={ctx} openId={id} />}
        {page === "tasks" && <TasksPage ctx={ctx} />}
        {page === "messages" && <MessagesPage ctx={ctx} />}
        {page === "payers" && <PayersPage slug={id} />}
        {page === "account" && <><AppearanceCard /><PasswordCard /></>}
        {page === "admin" && (ctx.role === "owner" || ctx.role === "admin" ? <AdminPage ctx={ctx} tab={id} /> : (go(""), null))}
      </main>
    </div>
  );
}
